/**
 * Fetch Ghost SSO URL from Control Plane API
 *
 * Mirrors fetchChatwootSso.ts pattern exactly.
 * GOTCHA: Control Plane JWT auth for allow_guest endpoints requires:
 *   1. NO Authorization header (Frappe rejects unknown Bearer tokens)
 *   2. access_token passed as body parameter
 */

import { getCPToken } from "@/lib/api/controlPlaneClient";
import type { GhostSsoResponse } from "./types";

const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || "https://control.zaviago.com";

export async function fetchGhostSso(
  tenantName: string,
  userEmail: string
): Promise<GhostSsoResponse> {
  const token = getCPToken();
  if (!token) {
    throw new Error("Not authenticated to Control Plane");
  }

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.ghost.get_sso_url`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "omit",
      body: JSON.stringify({
        access_token: token,
        tenant_name: tenantName,
        email: userEmail,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    let message = "Failed to get Ghost SSO URL";
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        message =
          typeof msgs[0] === "string" ? msgs[0] : JSON.stringify(msgs[0]);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    } else if (error.exc_type) {
      message = `${error.exc_type}: ${error.exc || "Unknown error"}`;
    }
    throw new Error(message);
  }

  const data = await response.json();
  return data.message as GhostSsoResponse;
}
